import {
  Badge,
  Icon,
  InlineStack,
  Stack,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
  VisuallyHidden,
} from '@customerio/pluma-components/react';
import type { PlanCell, PlanRow, PlanRowGroup } from '../content';
import * as styles from './plan-table.css';

type PlanTableProps = {
  caption: string;
  groups: PlanRowGroup[];
};

export function PlanTable({ caption, groups }: PlanTableProps) {
  return (
    <Table caption={caption} layout="fixed">
      <TableThead>
        <TableTr>
          <TableTh className={styles.labelColumn}>What</TableTh>
          <TableTh>Free to start</TableTh>
          <TableTh>Pay as you go</TableTh>
        </TableTr>
      </TableThead>
      <TableTbody>
        {groups.map((group, index) => (
          <PlanRowGroupRows key={group.name ?? index} group={group} />
        ))}
      </TableTbody>
    </Table>
  );
}

type PlanRowGroupRowsProps = {
  group: PlanRowGroup;
};

function PlanRowGroupRows({ group }: PlanRowGroupRowsProps) {
  return (
    <>
      {group.name == null ? null : (
        <TableTr>
          <TableTd colSpan={3}>
            <Text size="sm" weight="semibold">
              {group.name}
            </Text>
          </TableTd>
        </TableTr>
      )}
      {group.rows.map((row) => (
        <PlanTableRow key={row.label} row={row} />
      ))}
    </>
  );
}

type PlanTableRowProps = {
  row: PlanRow;
};

function PlanTableRow({ row }: PlanTableRowProps) {
  return (
    <TableTr>
      <TableTd>
        <Stack gap="025">
          <InlineStack gap="075" alignBlock="center">
            <Text size="sm" weight="medium">
              {row.label}
            </Text>
            {row.tag == null ? null : (
              <Badge color="accent" flexShrink="0" variant="solid">
                {row.tag}
              </Badge>
            )}
          </InlineStack>
          {row.note == null ? null : (
            <Text size="xs" color="secondary">
              {row.note}
            </Text>
          )}
        </Stack>
      </TableTd>
      <TableTd>
        <PlanCellValue cell={row.free} />
      </TableTd>
      <TableTd>
        <PlanCellValue cell={row.payAsYouGo} />
      </TableTd>
    </TableTr>
  );
}

type PlanCellValueProps = {
  cell: PlanCell;
};

function PlanCellValue({ cell }: PlanCellValueProps) {
  if (typeof cell === 'string') return <Text size="sm">{cell}</Text>;

  switch (cell.kind) {
    case 'included':
      return <Icon name="check" color="success" label="Included" />;
    case 'not-included':
      return (
        <>
          <Text size="sm" color="secondary" aria-hidden="true">
            —
          </Text>
          <VisuallyHidden>Not included</VisuallyHidden>
        </>
      );
    default: {
      const _exhaustive: never = cell;
      return String(_exhaustive);
    }
  }
}
